import Echo from 'laravel-echo'
import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'
import Pusher from 'pusher-js'
import type { ChannelAuthorizationCallback } from 'pusher-js'

import { useNotificationStore } from '~/store/notification'
import { isRecord } from '~/types/api'

interface BroadcastChannel {
  name: string
}

function getXsrfToken(): string {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : ''
}

type ChannelAuthData = NonNullable<Parameters<ChannelAuthorizationCallback>[1]>

function isChannelAuthData(value: unknown): value is ChannelAuthData {
  if (!isRecord(value)) return false
  if (typeof value.auth !== 'string' || !value.auth.trim()) return false
  if (value.channel_data !== undefined && typeof value.channel_data !== 'string') return false
  if (value.shared_secret !== undefined && typeof value.shared_secret !== 'string') return false
  return true
}

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  const config = useRuntimeConfig()

  window.Pusher = Pusher

  const echo = new Echo<'pusher'>({
    broadcaster: 'pusher',
    key: config.public.pusherKey,
    cluster: config.public.pusherCluster,
    forceTLS: true,
    namespace: undefined,
    authEndpoint: `${config.public.apiUrl}/broadcasting/auth`,
    withCredentials: true,
    enabledTransports: ['ws', 'wss'],
    authorizer: (channel: BroadcastChannel) => ({
      authorize: (socketId: string, callback: ChannelAuthorizationCallback) => {
        void fetch(`${config.public.apiUrl}/broadcasting/auth`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-XSRF-TOKEN': getXsrfToken(),
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name,
          }),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Broadcasting auth failed: ${response.status.toString()}`)
            }
            return response.json() as Promise<unknown>
          })
          .then((data: unknown) => {
            if (!isChannelAuthData(data)) {
              callback(new Error('Broadcasting auth failed: invalid payload.'), null)
              return
            }
            callback(null, data)
          })
          .catch((error: unknown) => {
            callback(error instanceof Error ? error : new Error(String(error)), null)
          })
      },
    }),
  })

  window.Echo = echo

  /** Public channel: site-wide promos / alerts (`App\Events\Broadcast\NotifyAll`). */
  if (window.__auctionNotifyAllBound !== true) {
    window.__auctionNotifyAllBound = true
    const notificationStore = useNotificationStore()
    echo.channel('notification').listen('.notification', (payload: { message?: string; type?: string }) => {
      notificationStore.methods.showBroadcastAnnouncement({
        message: payload.message ?? '',
        type: payload.type,
      })
    })
  }
})
